import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { TransformMode } from '@abyss/3d-viewer';
import { CuboidClickMode } from '@/types';
import { CuboidBase, CuboidState, CuboidStyleHandlers } from '../types';
import { useMakeStyledCuboid } from './useMakeStyledCuboid';

export const useSelectCuboid = <CuboidT extends CuboidBase>(
  cuboidState: CuboidState<CuboidT>,
  styleHandlers: CuboidStyleHandlers<CuboidT>
) => {
  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);
  const [cuboidTransformMode, setCuboidTransformMode] = useAtom(cuboidState.cuboidTransformMode);
  const setCuboidClickMode = useSetAtom(cuboidState.cuboidClickMode);
  const setIsCuboidEditorEnabled = useSetAtom(cuboidState.isCuboidEditorEnabled);

  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const selectCuboid = useCallback(
    (cuboid: CuboidT) => {
      if (
        currentCuboid &&
        currentCuboid.id === cuboid.id &&
        cuboidTransformMode === TransformMode.View
      ) {
        setCurrentCuboid(undefined);
        setIsCuboidEditorEnabled(false);
        return;
      }
      const styledCuboid = makeStyledCuboid(cuboid);

      setCurrentCuboid(styledCuboid);
      setCuboidTransformMode(TransformMode.BoxResize);
      setIsCuboidEditorEnabled(true);
      setCuboidClickMode(CuboidClickMode.Default);
    },
    [
      currentCuboid,
      setCurrentCuboid,
      makeStyledCuboid,
      cuboidTransformMode,
      setCuboidTransformMode,
      setIsCuboidEditorEnabled,
      setCuboidClickMode,
    ]
  );

  return selectCuboid;
};
